<template>
  <div :class="['p2p-delivery', { compact }]">
    <template v-if="!compact">
      <MediaImg v-if="delivery.thumb_url" :src="delivery.thumb_url" ratio="4 / 5" class="p2p-delivery-thumb" />
      <span v-else class="p2p-delivery-thumb text"><v-icon small>mdi-text</v-icon></span>
    </template>
    <div class="p2p-stack" style="gap:4px;min-width:0;flex:1 1 220px">
      <div class="p2p-row" style="gap:8px">
        <NetBadge v-if="delivery.platform" :platform="delivery.platform" />
        <span class="p2p-h3">{{ delivery.account ? (delivery.account.display_name || delivery.account.username) : delivery.platform_label }}</span>
        <span v-if="showProject" class="p2p-faint">· {{ delivery.project_name }}</span>
      </div>
      <div v-if="!compact" class="p2p-muted" style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{{ delivery.hook || delivery.caption }}</div>
      <div v-if="numbers.length" class="p2p-row p2p-metrics" style="gap:10px" :title="metricsTitle">
        <span v-for="item in numbers" :key="item.key" class="p2p-faint"><v-icon x-small>{{ item.icon }}</v-icon> {{ item.value }}</span>
      </div>
      <div v-else-if="delivery.state === 'published' && delivery.metrics && delivery.metrics.unavailable" class="p2p-faint">{{ delivery.metrics.unavailable }}</div>
      <div v-if="delivery.error && (delivery.state === 'failed' || delivery.state === 'needs_credits' || delivery.state === 'scheduled' || delivery.state === 'cancelled')" class="p2p-faint" :class="{ 'p2p-bad': (delivery.state === 'failed' || delivery.state === 'needs_credits') && !unconfirmed }">{{ errorText }}</div>
    </div>
    <div class="p2p-stack" style="gap:6px;align-items:flex-end">
      <span :class="['p2p-chip', tone]"><span :class="['p2p-dot', dot]"></span>{{ stateLabel }}</span>
      <span class="p2p-faint p2p-mono">{{ timeLabel }}</span>
    </div>
    <div class="p2p-row" style="gap:6px">
      <a v-if="delivery.public_url && !demoLink" :href="delivery.public_url" target="_blank" rel="noopener" class="p2p-btn sm soft">View post <v-icon x-small>mdi-open-in-new</v-icon></a>
      <span v-else-if="delivery.public_url" class="p2p-chip mono" :title="delivery.public_url">Demo link</span>
      <button v-if="delivery.state === 'failed' || delivery.state === 'needs_credits'" type="button" class="p2p-btn sm" :disabled="busy" @click="retry">Retry</button>
      <button v-if="delivery.state === 'scheduled'" type="button" class="p2p-btn sm ghost" :disabled="busy" @click="startMove">Move</button>
      <button v-if="delivery.state === 'scheduled'" type="button" class="p2p-btn sm ghost" :disabled="busy" @click="cancel">Cancel</button>
    </div>
    <div v-if="moving" class="p2p-row" style="flex-basis:100%;gap:8px;align-items:flex-end">
      <label class="p2p-label" style="flex:0 1 260px">New time <span class="p2p-faint">({{ zone }})</span>
        <input v-model="moveAt" type="datetime-local" class="p2p-input">
      </label>
      <button type="button" class="p2p-btn sm" :disabled="busy || !moveAt" @click="move">Save</button>
      <button type="button" class="p2p-btn sm ghost" :disabled="busy" @click="moving = false">Keep the time</button>
    </div>
    <div v-if="error" class="p2p-faint p2p-bad" style="flex-basis:100%">{{ error }}</div>
  </div>
</template>

<script>
import MediaImg from './MediaImg.vue';
import NetBadge from './NetBadge.vue';
import { post, patch, message } from '../api';
import { localDate } from '../poll';

const STATES = { scheduled: ['Scheduled', '', ''], queued: ['Starting', '', 'is-accent pulse'], publishing: ['Publishing', '', 'is-accent pulse'],
  // needs_credits may really mean "needs a plan": the row's error text carries the stored reason.
  published: ['Published', 'good', 'good'], failed: ['Failed', 'bad', 'bad'], needs_credits: ['Waiting', 'warn', 'warn'],
  cancelled: ['Cancelled', '', ''], retried: ['Retried', '', ''] };

export default {
  name: 'DeliveryRow',
  components: { MediaImg, NetBadge },
  props: { delivery: { type: Object, required: true }, showProject: { type: Boolean, default: false }, compact: { type: Boolean, default: false } },
  data: () => ({ busy: false, error: '', moving: false, moveAt: '' }),
  computed: {
    zone() { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'your time zone'; },
    entry() {
      // A now-post behind another publish to the same account goes out on its own, in order, and a
      // transient network rejection is retried without the member's help.
      if (this.delivery.state === 'scheduled' && this.delivery.mode === 'now') return ['Waiting its turn', '', ''];
      if (this.delivery.retry_pending) return ['Retrying', 'warn', 'warn'];
      if (this.delivery.state === 'retried' && this.delivery.error_code === 'network_transient') return ['Retried automatically', '', ''];
      return STATES[this.delivery.state] || [this.delivery.state, '', ''];
    },
    stateLabel() { return this.entry[0]; },
    tone() { return this.entry[1]; },
    dot() { return this.entry[2]; },
    numbers() {
      const m = this.delivery.metrics;
      if (!m || m.pending || m.unavailable) return [];
      const icons = { views: 'mdi-eye-outline', reactions: 'mdi-heart-outline', comments: 'mdi-comment-outline',
        shares: 'mdi-repeat', saves: 'mdi-bookmark-outline', clicks: 'mdi-cursor-default-click-outline' };
      return Object.keys(icons).filter(key => m[key] !== null && m[key] !== undefined)
        .map(key => ({ key, icon: icons[key], value: m[key] >= 10000 ? Math.round(m[key] / 1000) + 'k' : m[key] }));
    },
    metricsTitle() {
      const m = this.delivery.metrics || {};
      return ['views', 'reactions', 'comments', 'shares', 'saves', 'clicks'].filter(key => m[key] !== null && m[key] !== undefined)
        .map(key => key + ': ' + m[key]).join(', ') + (this.delivery.metrics_at ? ' · updated ' + localDate(this.delivery.metrics_at) : '');
    },
    demoLink() { return /example\.com\/publisher-demo\//.test(this.delivery.public_url || ''); },
    // The platform could not confirm the outcome: we check with the network before any retry.
    unconfirmed() { return this.delivery.state === 'failed' && /^We couldn't confirm/.test(this.delivery.error || ''); },
    errorText() {
      if (this.unconfirmed) return 'We could not confirm whether this went out, and you were not charged. Retry checks with the network first, so it is never posted twice.';
      if (this.delivery.error === 'Account disconnected') return 'Cancelled because the account was disconnected.';
      if (this.delivery.state === 'failed' && this.delivery.attempts > 0) return this.delivery.error + ' (we retried automatically)';
      return this.delivery.error;
    },
    timeLabel() {
      if (this.delivery.state === 'published') return localDate(this.delivery.published_at);
      if (this.delivery.state === 'scheduled') return localDate(this.delivery.scheduled_at);
      return '';
    }
  },
  methods: {
    async act(action) {
      this.busy = true;
      this.error = '';
      try { this.$emit('changed', await post('/deliveries/' + this.delivery.id + '/' + action)); } catch (error) { this.error = message(error); } finally { this.busy = false; }
    },
    retry() { return this.act('retry'); },
    cancel() { return this.act('cancel'); },
    startMove() {
      // The picker shows local wall time, like the Schedule step.
      const at = new Date(this.delivery.scheduled_at);
      const pad = value => String(value).padStart(2, '0');
      this.moveAt = at.getFullYear() + '-' + pad(at.getMonth() + 1) + '-' + pad(at.getDate()) + 'T' + pad(at.getHours()) + ':' + pad(at.getMinutes());
      this.moving = true;
    },
    async move() {
      this.busy = true;
      this.error = '';
      try {
        this.$emit('changed', await patch('/deliveries/' + this.delivery.id, { scheduled_at: new Date(this.moveAt).toISOString() }));
        this.moving = false;
      } catch (error) {
        this.error = message(error);
      } finally {
        this.busy = false;
      }
    }
  }
};
</script>

<style>
.p2p-delivery{display:flex;flex-wrap:wrap;align-items:center;gap:14px;padding:12px 14px;border:1px solid var(--p2p-line);border-radius:14px;background:var(--p2p-panel)}
.p2p-delivery.compact{padding:8px 12px;background:var(--p2p-card)}
.p2p-delivery-thumb{flex:0 0 auto;width:48px!important;border-radius:9px}
.p2p-delivery-thumb.text{display:inline-grid;place-items:center;height:60px;background:var(--p2p-raised);color:var(--p2p-muted)}
</style>
